import { Form } from 'react-bootstrap';
import './AlertForm.scss';
import * as R from 'ramda';
import { Dropdown } from '@Components/Dropdown';
import { Checkbox } from '@Components/FormCheck';
import { Size } from '@Components/designConstants';
import { SegmentedControlButton } from '@Components/SegmentedControlButton';
import { InfoTooltip } from '@Components/InfoTooltip/InfoTooltip';
import { AlertFormData } from '../anomalyDetectionConstants';
import { FrequencySection } from '../FrequencySection';
import { matchOptionValue, matchOptionValues, SECTION_CHECKBOX_PROPS } from '../utils/alertUtils';

interface PresetFormProps {
  formData: AlertFormData;
  onFormDataChange: (formData: AlertFormData) => void;
  presetName: string;
  onPresetNameChange: (name: string) => void;
  description: string;
  onDescriptionChange: (description: string) => void;
  emailAddressOptions: { value: string; label: string }[];
  slackMemberOptions: { value: string; label: string }[];
  slackChannelOptions: { value: string; label: string }[];
  isLoadingEmailAddresses: boolean;
  isLoadingSlackMembers: boolean;
  isLoadingSlackChannels: boolean;
  emailAddressError: unknown;
  slackMembersError: unknown;
  slackChannelsError: unknown;
  fieldErrors?: Record<string, string>;
}

export const PresetForm = ({
  formData,
  onFormDataChange,
  presetName,
  onPresetNameChange,
  description,
  onDescriptionChange,
  emailAddressOptions,
  slackMemberOptions,
  slackChannelOptions,
  isLoadingEmailAddresses,
  isLoadingSlackMembers,
  isLoadingSlackChannels,
  emailAddressError,
  slackMembersError,
  slackChannelsError,
  fieldErrors = {}
}: PresetFormProps) => {
  const spendCadenceOptions = [{ value: 'Evenly distributed', label: 'Evenly distributed' }];

  const handleInputChange = (section: string, field: string, value: string | boolean) => {
    const newFormData = R.clone(formData);
    const data = newFormData as unknown as Record<string, unknown>;

    if (section.includes('.')) {
      const [parentSection, childSection] = section.split('.');
      const parentData = data[parentSection] as Record<string, unknown>;
      data[parentSection] = {
        ...parentData,
        [childSection]: {
          ...(parentData[childSection] as Record<string, unknown>),
          [field]: value
        }
      };
    } else {
      data[section] = {
        ...(data[section] as Record<string, unknown>),
        [field]: value
      };
    }

    onFormDataChange(newFormData);
  };

  const handleCheckboxChange = (section: string, enabled: boolean) => {
    const newFormData = R.clone(formData);
    const data = newFormData as unknown as Record<string, unknown>;
    data[section] = {
      ...(data[section] as Record<string, unknown>),
      enabled
    };
    onFormDataChange(newFormData);
  };

  const handleSimpleInputChange = (field: string, value: string | boolean) => {
    const newFormData = R.clone({ ...formData, [field]: value });
    onFormDataChange(newFormData);
  };

  return (
    <Form>
      <div className='alert-form'>
        <div className='form-section'>
          <h4 className='section-title'>
            Preset Details <span className='asterisk'>*</span>
          </h4>
          <p className='section-description'>
            Give your preset a name and description to help identify it later.
          </p>

          <Form.Group>
            <Form.Label>Preset Name</Form.Label>
            <Form.Control
              type='text'
              value={presetName}
              onChange={e => onPresetNameChange(e.target.value)}
              placeholder='Enter preset name...'
              className='form-control'
            />
            {fieldErrors?.presetName && (
              <small className='form-text text-danger'>{fieldErrors.presetName}</small>
            )}
          </Form.Group>

          <Form.Group>
            <Form.Label>Description</Form.Label>
            <Form.Control
              as='textarea'
              rows={3}
              value={description}
              onChange={e => onDescriptionChange(e.target.value)}
              placeholder='Enter description...'
              className='form-control'
            />
            {fieldErrors?.description && (
              <small className='form-text text-danger'>{fieldErrors.description}</small>
            )}
          </Form.Group>
        </div>

        <div className='form-section'>
          <div className='horizontal-line' />
        </div>

        <div className='form-section'>
          <h4 className='section-title'>
            Spend Cadence<span className='asterisk'>*</span>
          </h4>
          <p className='section-description'>
            Currently only supporting <strong>evenly distributed</strong> spend
          </p>
          <Form.Group>
            <Dropdown
              searchable
              ariaLabel='Spend cadence'
              placeholder='Select spend cadence...'
              options={spendCadenceOptions}
              value={matchOptionValue(spendCadenceOptions, formData.spendCadence)}
              onChange={spendCadence => handleSimpleInputChange('spendCadence', spendCadence ?? '')}
              reactSelectProps={{ noOptionsMessage: () => 'No spend cadence options available' }}
            />
          </Form.Group>
        </div>

        <div className='form-section'>
          <div className='section-header'>
            <Checkbox
              checked={formData.actual.enabled}
              onChange={e => handleCheckboxChange('actual', e.target.checked)}
              {...SECTION_CHECKBOX_PROPS}
            />
            <h4 className='section-title'>Actual</h4>
          </div>
          <p className='section-description'>
            Monitor actual spend performance against budget pacing expectations
          </p>
          {formData.actual.enabled && (
            <>
              <div className='threshold-inputs'>
                <Form.Group>
                  <Form.Label>Under:</Form.Label>
                  <div className='threshold-input-with-toggle'>
                    <Form.Control
                      type='number'
                      value={formData.actual.under.replace(/[^0-9]/g, '')}
                      onChange={e => {
                        const { value } = e.target;
                        const { thresholdType } = formData.actual;
                        const suffix = thresholdType === 'dollar' ? '$' : '%';
                        handleInputChange('actual', 'under', `${value}${suffix}`);
                      }}
                      className='threshold-number-input'
                      placeholder='20'
                    />
                    <SegmentedControlButton
                      options={['$', '%']}
                      selectedOption={formData.actual.thresholdType === 'dollar' ? '$' : '%'}
                      onChange={value => {
                        const thresholdType = value === '$' ? 'dollar' : 'percentage';
                        handleInputChange('actual', 'thresholdType', thresholdType);
                      }}
                      size={Size.SM}
                    />
                  </div>
                  <small className='form-text text-muted'>
                    The amount below expected (ex: 20% below 100% or $100 below goal amount)
                  </small>
                  {fieldErrors?.actualUnder && (
                    <div>
                      <small className='form-text text-danger'>{fieldErrors.actualUnder}</small>
                    </div>
                  )}
                </Form.Group>
                <Form.Group>
                  <Form.Label>Over:</Form.Label>
                  <div className='threshold-input-with-toggle'>
                    <Form.Control
                      type='number'
                      value={formData.actual.over.replace(/[^0-9]/g, '')}
                      onChange={e => {
                        const { value } = e.target;
                        const { thresholdType } = formData.actual;
                        const suffix = thresholdType === 'dollar' ? '$' : '%';
                        handleInputChange('actual', 'over', `${value}${suffix}`);
                      }}
                      className='threshold-number-input'
                      placeholder='20'
                    />
                    <SegmentedControlButton
                      options={['$', '%']}
                      selectedOption={formData.actual.thresholdType === 'dollar' ? '$' : '%'}
                      onChange={value => {
                        const thresholdType = value === '$' ? 'dollar' : 'percentage';
                        handleInputChange('actual', 'thresholdType', thresholdType);
                      }}
                      size={Size.SM}
                    />
                  </div>
                  <small className='form-text text-muted'>
                    The amount above expected (ex: 20% above 100% or $100 above goal amount)
                  </small>
                  {fieldErrors?.actualOver && (
                    <div>
                      <small className='form-text text-danger'>{fieldErrors.actualOver}</small>
                    </div>
                  )}
                </Form.Group>
              </div>
              <div className='budget-type-toggle'>
                <Form.Group>
                  <div className='budget-type-toggle-width'>
                    <SegmentedControlButton
                      options={['Total Budget', 'Ideal Spend']}
                      selectedOption={
                        formData.actual.budgetType === 'total' ? 'Total Budget' : 'Ideal Spend'
                      }
                      onChange={value => {
                        const budgetType = value === 'Total Budget' ? 'total' : 'ideal';
                        handleInputChange('actual', 'budgetType', budgetType);
                      }}
                      size={Size.SM}
                    />
                  </div>
                </Form.Group>
              </div>
              <div className='frequency-section'>
                <Form.Group>
                  <FrequencySection
                    frequencyData={formData.actual.frequency}
                    onFrequencyChange={(field, value) =>
                      handleInputChange('actual.frequency', field, value)
                    }
                    label='Frequency Type:'
                  />
                </Form.Group>
              </div>
              <div className='always-notify'>
                <Form.Group>
                  <Checkbox
                    checked={formData.actual.alwaysNotify}
                    onChange={e => handleInputChange('actual', 'alwaysNotify', e.target.checked)}
                    label='Always Notify (get alerted with a status even when the checks pass)'
                  />
                </Form.Group>
              </div>
            </>
          )}
        </div>

        <div className='form-section'>
          <div className='section-header'>
            <Checkbox
              checked={formData.projected.enabled}
              onChange={e => handleCheckboxChange('projected', e.target.checked)}
              {...SECTION_CHECKBOX_PROPS}
            />
            <h4 className='section-title'>Projected</h4>
          </div>
          <p className='section-description'>
            Track projected spend based on current delivery patterns and trends
          </p>
          {formData.projected.enabled && (
            <>
              <div className='threshold-inputs'>
                <Form.Group>
                  <Form.Label>Under:</Form.Label>
                  <div className='threshold-input-with-toggle'>
                    <Form.Control
                      type='number'
                      value={formData.projected.under.replace(/[^0-9]/g, '')}
                      onChange={e => {
                        const { value } = e.target;
                        const { thresholdType } = formData.projected;
                        const suffix = thresholdType === 'dollar' ? '$' : '%';
                        handleInputChange('projected', 'under', `${value}${suffix}`);
                      }}
                      className='threshold-number-input'
                      placeholder='20'
                    />
                    <SegmentedControlButton
                      options={['$', '%']}
                      selectedOption={formData.projected.thresholdType === 'dollar' ? '$' : '%'}
                      onChange={value => {
                        const thresholdType = value === '$' ? 'dollar' : 'percentage';
                        handleInputChange('projected', 'thresholdType', thresholdType);
                      }}
                      size={Size.SM}
                    />
                  </div>
                  <small className='form-text text-muted'>
                    The amount below expected (ex: 20% below 100% or $100 below goal amount)
                  </small>
                  {fieldErrors?.projectedUnder && (
                    <div>
                      <small className='form-text text-danger'>{fieldErrors.projectedUnder}</small>
                    </div>
                  )}
                </Form.Group>
                <Form.Group>
                  <Form.Label>Over:</Form.Label>
                  <div className='threshold-input-with-toggle'>
                    <Form.Control
                      type='number'
                      value={formData.projected.over.replace(/[^0-9]/g, '')}
                      onChange={e => {
                        const { value } = e.target;
                        const { thresholdType } = formData.projected;
                        const suffix = thresholdType === 'dollar' ? '$' : '%';
                        handleInputChange('projected', 'over', `${value}${suffix}`);
                      }}
                      className='threshold-number-input'
                      placeholder='20'
                    />
                    <SegmentedControlButton
                      options={['$', '%']}
                      selectedOption={formData.projected.thresholdType === 'dollar' ? '$' : '%'}
                      onChange={value => {
                        const thresholdType = value === '$' ? 'dollar' : 'percentage';
                        handleInputChange('projected', 'thresholdType', thresholdType);
                      }}
                      size={Size.SM}
                    />
                  </div>
                  <small className='form-text text-muted'>
                    The amount above expected (ex: 20% above 100% or $100 below goal amount)
                  </small>
                  {fieldErrors?.projectedOver && (
                    <div>
                      <small className='form-text text-danger'>{fieldErrors.projectedOver}</small>
                    </div>
                  )}
                </Form.Group>
                <div className='frequency-section'>
                  <Form.Group>
                    <FrequencySection
                      frequencyData={formData.projected.frequency}
                      onFrequencyChange={(field, value) =>
                        handleInputChange('projected.frequency', field, value)
                      }
                      label='Frequency Type:'
                    />
                  </Form.Group>
                </div>
              </div>
              <div className='always-notify'>
                <Form.Group>
                  <Checkbox
                    checked={formData.projected.alwaysNotify}
                    onChange={e => handleInputChange('projected', 'alwaysNotify', e.target.checked)}
                    label='Always Notify (get alerted with a status even when the checks pass)'
                  />
                </Form.Group>
              </div>
            </>
          )}
        </div>

        <div className='form-section'>
          <div className='section-header'>
            <Checkbox
              checked={formData.remainder.enabled}
              onChange={e => handleCheckboxChange('remainder', e.target.checked)}
              {...SECTION_CHECKBOX_PROPS}
            />
            <h4 className='section-title'>Remainder</h4>
          </div>
          <p className='section-description'>
            Monitor remaining budget allocation and pacing to goal completion
          </p>
          {formData.remainder.enabled && (
            <>
              <div className='threshold-inputs'>
                <Form.Group>
                  <Form.Label>Under:</Form.Label>
                  <div className='threshold-input-with-toggle'>
                    <Form.Control
                      type='number'
                      value={formData.remainder.under.replace(/[^0-9]/g, '')}
                      onChange={e => {
                        const { value } = e.target;
                        const { thresholdType } = formData.remainder;
                        const suffix = thresholdType === 'dollar' ? '$' : '%';
                        handleInputChange('remainder', 'under', `${value}${suffix}`);
                      }}
                      className='threshold-number-input'
                      placeholder='50'
                    />
                    <SegmentedControlButton
                      options={['$', '%']}
                      selectedOption={formData.remainder.thresholdType === 'dollar' ? '$' : '%'}
                      onChange={value => {
                        const thresholdType = value === '$' ? 'dollar' : 'percentage';
                        handleInputChange('remainder', 'thresholdType', thresholdType);
                      }}
                      size={Size.SM}
                    />
                  </div>
                  <small className='form-text text-muted'>
                    The amount below expected (ex: 20% below 100% or $100 below goal amount)
                  </small>
                  {fieldErrors?.remainderUnder && (
                    <div>
                      <small className='form-text text-danger'>{fieldErrors.remainderUnder}</small>
                    </div>
                  )}
                </Form.Group>
                <Form.Group>
                  <Form.Label>Over:</Form.Label>
                  <div className='threshold-input-with-toggle'>
                    <Form.Control
                      type='number'
                      value={formData.remainder.over.replace(/[^0-9]/g, '')}
                      onChange={e => {
                        const { value } = e.target;
                        const { thresholdType } = formData.remainder;
                        const suffix = thresholdType === 'dollar' ? '$' : '%';
                        handleInputChange('remainder', 'over', `${value}${suffix}`);
                      }}
                      className='threshold-number-input'
                      placeholder='100'
                    />
                    <SegmentedControlButton
                      options={['$', '%']}
                      selectedOption={formData.remainder.thresholdType === 'dollar' ? '$' : '%'}
                      onChange={value => {
                        const thresholdType = value === '$' ? 'dollar' : 'percentage';
                        handleInputChange('remainder', 'thresholdType', thresholdType);
                      }}
                      size={Size.SM}
                    />
                  </div>
                  <small className='form-text text-muted'>
                    The amount above expected (ex: 20% above 100% or $100 above goal amount)
                  </small>
                  {fieldErrors?.remainderOver && (
                    <div>
                      <small className='form-text text-danger'>{fieldErrors.remainderOver}</small>
                    </div>
                  )}
                </Form.Group>
                <div className='frequency-section'>
                  <Form.Group>
                    <FrequencySection
                      frequencyData={formData.remainder.frequency}
                      onFrequencyChange={(field, value) =>
                        handleInputChange('remainder.frequency', field, value)
                      }
                      label='Frequency Type:'
                    />
                  </Form.Group>
                </div>
              </div>
              <div className='always-notify'>
                <Form.Group>
                  <Checkbox
                    checked={formData.remainder.alwaysNotify}
                    onChange={e => handleInputChange('remainder', 'alwaysNotify', e.target.checked)}
                    label='Always Notify (get alerted with a status even when the checks pass)'
                  />
                </Form.Group>
              </div>
            </>
          )}
        </div>

        {fieldErrors?.monitoringType && (
          <div className='form-section'>
            <small className='form-text text-danger'>{fieldErrors.monitoringType}</small>
          </div>
        )}

        <div className='form-section'>
          <div className='horizontal-line' />
          <h3 className='section-header-title'>
            Alert timing and destination<span className='asterisk'>*</span>
          </h3>
        </div>

        {fieldErrors?.notificationMethod && (
          <div className='form-section'>
            <small className='form-text text-danger'>{fieldErrors.notificationMethod}</small>
          </div>
        )}

        <div className='form-section'>
          <div className='section-header'>
            <Checkbox
              checked={formData.slack.enabled}
              onChange={e => handleCheckboxChange('slack', e.target.checked)}
              {...SECTION_CHECKBOX_PROPS}
            />
            <h4 className='section-title'>Slack</h4>
            <InfoTooltip size={Size.SM}>
              If you don&apos;t see your slack channel, please create it in Slack first. For
              security and premissioning reasons, we cannot create channels outside of Slack.
            </InfoTooltip>
          </div>
          {formData.slack.enabled && (
            <div className='notification-inputs'>
              <Form.Group>
                <Form.Label>Slack Channel:</Form.Label>
                {isLoadingSlackChannels && (
                  <div className='loading-indicator'>
                    <small className='form-text text-muted'>Loading Slack channels...</small>
                  </div>
                )}
                {Boolean(slackChannelsError) && (
                  <div className='error-message'>
                    <small className='form-text text-danger'>
                      Failed to load Slack channels. Using fallback options.
                    </small>
                  </div>
                )}
                <Dropdown
                  searchable
                  clearable
                  ariaLabel='Slack channel'
                  placeholder='Select Slack channel...'
                  options={slackChannelOptions}
                  value={matchOptionValue(slackChannelOptions, formData.slack.channels[0])}
                  onChange={channel => {
                    const channelId = channel ?? '';
                    const newFormData = R.clone({
                      ...formData,
                      slack: {
                        ...formData.slack,
                        channels: [channelId]
                      }
                    });
                    onFormDataChange(newFormData);
                  }}
                  reactSelectProps={{ noOptionsMessage: () => 'No Slack channels available' }}
                />
                {fieldErrors?.slackChannels && (
                  <small className='form-text text-danger'>{fieldErrors.slackChannels}</small>
                )}
              </Form.Group>
              <Form.Group>
                <Form.Label>Slack Member(s):</Form.Label>
                {isLoadingSlackMembers && (
                  <div className='loading-indicator'>
                    <small className='form-text text-muted'>Loading Slack members...</small>
                  </div>
                )}
                {Boolean(slackMembersError) && (
                  <div className='error-message'>
                    <small className='form-text text-danger'>
                      Failed to load Slack members. Using fallback options.
                    </small>
                  </div>
                )}
                <Dropdown
                  searchable
                  multiSelect
                  showChips
                  clearable
                  ariaLabel='Slack members'
                  placeholder='Select Slack members...'
                  options={slackMemberOptions}
                  value={matchOptionValues(slackMemberOptions, formData.slack.members)}
                  onChange={memberIds => {
                    const newFormData = R.clone({
                      ...formData,
                      slack: {
                        ...formData.slack,
                        members: memberIds
                      }
                    });
                    onFormDataChange(newFormData);
                  }}
                  reactSelectProps={{ noOptionsMessage: () => 'No Slack members available' }}
                />
                {fieldErrors?.slackMembers && (
                  <small className='form-text text-danger'>{fieldErrors.slackMembers}</small>
                )}
              </Form.Group>
            </div>
          )}
        </div>

        <div className='form-section'>
          <div className='section-header'>
            <Checkbox
              checked={formData.email.enabled}
              onChange={e => handleCheckboxChange('email', e.target.checked)}
              {...SECTION_CHECKBOX_PROPS}
            />
            <h4 className='section-title'>Email</h4>
          </div>
          {formData.email.enabled && (
            <Form.Group>
              <Form.Label>Email Address(es):</Form.Label>
              {isLoadingEmailAddresses && (
                <div className='loading-indicator'>
                  <small className='form-text text-muted'>Loading email addresses...</small>
                </div>
              )}
              {Boolean(emailAddressError) && (
                <div className='error-message'>
                  <small className='form-text text-danger'>
                    Failed to load email addresses. Using fallback options.
                  </small>
                </div>
              )}
              <Dropdown
                searchable
                multiSelect
                showChips
                clearable
                ariaLabel='Email addresses'
                placeholder='Select email addresses...'
                options={emailAddressOptions}
                value={matchOptionValues(emailAddressOptions, formData.email.addresses)}
                onChange={emailAddresses => {
                  const newFormData = R.clone({
                    ...formData,
                    email: {
                      ...formData.email,
                      addresses: emailAddresses
                    }
                  });
                  onFormDataChange(newFormData);
                }}
                reactSelectProps={{ noOptionsMessage: () => 'No email addresses available' }}
              />
              {fieldErrors?.emailAddresses && (
                <small className='form-text text-danger'>{fieldErrors.emailAddresses}</small>
              )}
            </Form.Group>
          )}
        </div>
      </div>
    </Form>
  );
};
